import { useEffect, useState } from "react";

import { useAppSelector } from "../app/hooks";
import {
  getFamilyId,
  isFamilyMember,
} from "../helper/contractFunctions/MafiaFamily";

export const checkIsFamilyMember = async (
  address: string,
  familyId: number
): Promise<boolean> => {
  if (!address || familyId <= 0) return false;

  try {
    return await isFamilyMember(address, familyId);
  } catch {
    return false;
  }
};

export const checkHasOwnFamily = async (address: string): Promise<boolean> => {
  if (!address) return false;

  try {
    const familyId = await getFamilyId(address);
    return checkIsFamilyMember(address, familyId);
  } catch {
    return false;
  }
};

/** Checks if the current user is a member of the given family (Family page pattern). */
const useIsFamilyMember = (familyId?: number | null) => {
  const { myProfile } = useAppSelector((state) => state.profile);
  const [isInFamily, setIsInFamily] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const result = await checkIsFamilyMember(
        myProfile.address ?? "",
        familyId ?? 0
      );
      if (!cancelled) setIsInFamily(result);
    };

    check();

    return () => {
      cancelled = true;
    };
  }, [myProfile.address, familyId]);

  return isInFamily;
};

/** Checks if the current user belongs to any family via contract. */
export const useIsInOwnFamily = () => {
  const { myProfile } = useAppSelector((state) => state.profile);
  const [isInFamily, setIsInFamily] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const result = await checkHasOwnFamily(myProfile.address ?? "");
      if (!cancelled) setIsInFamily(result);
    };

    check();

    return () => {
      cancelled = true;
    };
  }, [myProfile.address]);

  return isInFamily;
};

export default useIsFamilyMember;
